import type { ChartDatapointSelection } from '@/types/reports';

import type { LineChartOptions } from '../types';
import type { ResolvedMultiMeasureLayout } from '../utils/multiMeasureLayout';
import { ComboChartCase } from './ComboChartCase';
import {
  FacetedCartesianChart,
  type FacetedCartesianChartProps,
} from './FacetedCartesianChart';

interface MultiMeasureCartesianChartProps extends Omit<
  FacetedCartesianChartProps,
  'lineOptions'
> {
  layout: ResolvedMultiMeasureLayout;
  lineOptions: LineChartOptions;
  barStackId?: string;
  valueLeftLabel?: string;
  /** Called when the user clicks (or keyboard-activates) a chart datapoint. */
  onDatapointSelect?: (sel: ChartDatapointSelection) => void;
  /** The currently active/selected datapoint, used to dim all others. */
  activeSelection?: ChartDatapointSelection | null;
}

/**
 * Dispatches a chart with measures spanning more than one unit to its
 * resolved layout: `'combo'` renders bars-and-line on a shared x-axis via
 * ComboChartCase; `'panels'` renders one stacked panel per unit group via
 * FacetedCartesianChart. Never called with `'single'` — the bar/line/area
 * cases in ChartRendererCore only reach for this dispatcher once
 * resolveMultiMeasureLayout has ruled that out.
 */
export function MultiMeasureCartesianChart({
  layout,
  valueLeftLabel,
  barStackId,
  onDatapointSelect,
  activeSelection,
  ...facetedProps
}: MultiMeasureCartesianChartProps) {
  if (layout === 'combo') {
    return (
      <ComboChartCase
        chartType={facetedProps.chartType}
        unitGroups={facetedProps.unitGroups}
        chartData={facetedProps.chartData}
        config={facetedProps.config}
        columnTitles={facetedProps.columnTitles}
        columnTypes={facetedProps.columnTypes}
        currency={facetedProps.currency}
        seriesColors={facetedProps.seriesColors}
        height={facetedProps.height}
        legendAlign={facetedProps.legendAlign}
        tooltipFormatter={facetedProps.tooltipFormatter}
        categoryBottomLabel={facetedProps.categoryBottomLabel}
        valueLeftLabel={valueLeftLabel}
        lineOptions={facetedProps.lineOptions}
        barStackId={barStackId}
        onDatapointSelect={onDatapointSelect}
        activeSelection={activeSelection}
      />
    );
  }
  return (
    <FacetedCartesianChart
      {...facetedProps}
      onDatapointSelect={onDatapointSelect}
      activeSelection={activeSelection}
    />
  );
}
